import type { MemorySaveEntry } from "./memorySaveGate";

/**
 * The kind of a `contact_memory` fact, as a CODE (loop f item HELD).
 *
 * A held contradiction has to reach the owner who approves a draft (the
 * `pnpm drafts` list and show lines, the per-draft mail), and those surfaces
 * carry ids and codes only (section 10): never a stated value, never the
 * customer's words. So what the owner is told about a held row is which
 * kind of fact it is, and this is the one place that maps a row to that code.
 *
 * It reads ONLY the label the save-gate writes before the colon (`label:
 * value`, memorySaveGate.ts `Candidate.label`), never the value, and answers
 * from a closed list. A row the list does not read (a screened claim, a row
 * another writer wrote) is `other`, which is a safe answer: it names nothing.
 * `heldFactKind.test.ts` runs the real gate over every slot, so a label the
 * gate learns to write and this list does not know is a red test.
 */
export const HELD_FACT_KINDS = ["budget", "ring_size", "metal", "key_date", "other"] as const;
export type HeldFactKind = (typeof HELD_FACT_KINDS)[number];

/** `<label>(<owner>)?: ` at the start of a fact's content, the label from a closed list. */
const LABEL = /^(stated budget|ring size|metal preference|engagement|wedding|anniversary|birthday)(?: \((?:customer|her|him|them)\))?: /;

const KIND_OF_LABEL: Readonly<Record<string, HeldFactKind>> = {
  "stated budget": "budget",
  "ring size": "ring_size",
  "metal preference": "metal",
  engagement: "key_date",
  wedding: "key_date",
  anniversary: "key_date",
  birthday: "key_date",
};

/** The kind code of one memory row. Reads the label only; an inference is never a fact kind. */
export function heldFactKind(row: { readonly content: string; readonly isInference: boolean }): HeldFactKind {
  if (row.isInference) return "other";
  const m = LABEL.exec(row.content);
  return m === null ? "other" : (KIND_OF_LABEL[m[1]!] ?? "other");
}

/** The kind the gate itself files an entry's slot under: the other side of the round trip the test pins. */
export function heldFactKindOfSlot(slot: MemorySaveEntry["slot"]): HeldFactKind {
  switch (slot) {
    case "budget":
      return "budget";
    case "ring-size":
      return "ring_size";
    case "metal":
      return "metal";
    case "key-date":
      return "key_date";
    case "customer-claim":
      return "other";
  }
}
